import objectPath from 'object-path';
import { getExperimentsEnabled, getStreetAddress } from '../../../shared_modules/baui-utils';

const fieldsModel = require('./fieldsModel.json');
/*
  all prefill functions using the practice to overcome both linter and Object.assign nest overrride problem
  https://eslint.org/docs/rules/prefer-const
  https://eslint.org/docs/rules/no-param-reassign
  https://stackoverflow.com/questions/41588068/object-assign-override-nested-property
  */

function prefillExperianDM(fieldsModel, dmCode) {
  return Object.assign({}, fieldsModel, {
    loan: {
      ...fieldsModel.loan,
      // ExperianDM can only apply as individual
      loanType: 'individual',
    },
    dmCode: { code: dmCode }, // prefill dm funding code
  });
}

function prefillDM(fieldsModel, dmContext) {
  return Object.assign({}, fieldsModel, {
    dmCode: {
      // prefill dm funding code and lead type
      code: dmContext.code,
      leadType: dmContext.leadType,
      prescreenEntryId: dmContext.prescreenEntryId,
    },
  });
}

function prefillPrequal(fieldsModel, prequal) {
  // loanAmount, loanPurpose or creditScore may be undefined for partialPrequal
  return Object.assign({}, fieldsModel, {
    prequal: {
      ...fieldsModel.prequal,
      ...prequal,
    },
  });
}

function prefillSecondLoan(fieldsModel, context) {
  // note fieldsModel is just the copy of the template with no values in it
  return Object.assign({}, fieldsModel, {
    primary: {
      ...fieldsModel.primary,
      // bypass dob validation
      dob: { ...fieldsModel.primary.dob, monthBirth: undefined }, // copying empty str blank fields
    },
    email: objectPath.get(context.primaryBorrower, 'email', ''), // this is the only value from the server of current loan app in progress borrower
  });
}

function prefillSecondLoanJA(fieldsModel, context) {
  const { primaryBorrower, previousSecondaryBorrower } = context;
  return Object.assign({}, fieldsModel, {
    loan: {
      ...fieldsModel.loan,
    },
    primary: {
      ...fieldsModel.primary,
    },
    secondary: {
      ...fieldsModel.secondary,
      dob: {
        monthBirth: objectPath.get(previousSecondaryBorrower, 'monthBirth', ''),
        dayBirth: objectPath.get(previousSecondaryBorrower, 'dayBirth', ''),
        yearBirth: objectPath.get(previousSecondaryBorrower, 'yearBirth', ''),
      },
      name: {
        firstName: objectPath.get(previousSecondaryBorrower, 'firstName', ''),
        lastName: objectPath.get(previousSecondaryBorrower, 'lastName', ''),
      },
      address: {
        streetAddress: getStreetAddress(objectPath.get(previousSecondaryBorrower, 'address')),
        city: objectPath.get(previousSecondaryBorrower, 'address.city', ''),
        state: objectPath.get(previousSecondaryBorrower, 'address.state', ''),
        zip: objectPath.get(previousSecondaryBorrower, 'address.zip', ''),
        secondaryUsePrimaryAddress: false,
      },
    },
    email: objectPath.get(primaryBorrower, 'email', ''),
    emailSecondary: objectPath.get(previousSecondaryBorrower, 'email', ''),
  });
}

const prefillName = (fieldsModel, primaryBorrower) =>
  // much work to avoid fieldsModel.name setting directly
  Object.assign({}, fieldsModel, {
    primary: {
      ...fieldsModel.primary,
      // name of primary borrower
      name: {
        firstName: objectPath.get(primaryBorrower, 'firstName', ''),
        lastName: objectPath.get(primaryBorrower, 'lastName', ''),
      },
    },
  });

const prefillAddress = (fieldsModel, primaryBorrower) =>
  Object.assign({}, fieldsModel, {
    primary: {
      ...fieldsModel.primary,
      // address of primary borrower
      // NOTE: 2nd loan has street number and street separately!!
      address: {
        streetAddress: getStreetAddress(objectPath.get(primaryBorrower, 'address')),
        city: objectPath.get(primaryBorrower, 'address.city', ''),
        state: objectPath.get(primaryBorrower, 'address.state', ''),
        zip: objectPath.get(primaryBorrower, 'address.zip', ''),
      },
      homeStatus: {
        homeStatus: objectPath.get(primaryBorrower, 'address.ownership', ''),
      },
      homeStatusExpense: {
        homeStatusExpense: objectPath.get(primaryBorrower, 'address.rentAmount', ''),
      },
    },
  });

const isLocalStorageSupported = () => typeof Storage !== 'undefined';

/** Returns true if
 * - NODE_ENV is non-production
 * - HTML5 sessionStorage is supported
 * - shouldPrefill is set to true in sessionStorage
 *
 * @return {Boolean}
 */
const shouldPrefillWithMockData = ({ nodeEnv }) =>
  /^(?!prod).+/.test(nodeEnv) && isLocalStorageSupported() && sessionStorage.shouldPrefill === 'true';

/** Reads the mock form data from sessionStorage
 * and merges it with fieldsModel object
 *
 * @param {Object} fieldsModel
 * @return {Object}
 */
const prefillPI1WithMockData = fieldsModel => {
  const mockData = isLocalStorageSupported() && JSON.parse(sessionStorage.mockPI1FormData);
  return Object.assign({}, fieldsModel, mockData);
};

const prefillPrimaryEmail = (fieldsModel, primaryBorrower) => {
  if (!primaryBorrower?.email) return fieldsModel;

  return {
    ...fieldsModel,
    email: primaryBorrower.email,
  };
};

const prefillPrimaryDob = (fieldsModel, primaryBorrower) => {
  if (!primaryBorrower?.dob) return fieldsModel;

  return {
    ...fieldsModel,
    primary: {
      ...fieldsModel.primary,
      dob: primaryBorrower.dob,
    },
  };
};

const prefillPrimaryPhone = (fieldsModel, primaryBorrower) => {
  if (!primaryBorrower?.phone) return fieldsModel;

  return {
    ...fieldsModel,
    primary: {
      ...fieldsModel.primary,
      phone: primaryBorrower.phone,
    },
  };
};

const prefillPrimaryIncome = (fieldsModel, primaryBorrower) => {
  if (!primaryBorrower?.income) return fieldsModel;

  return {
    ...fieldsModel,
    primary: {
      ...fieldsModel.primary,
      income: {
        ...fieldsModel.primary.income,
        income: primaryBorrower.income,
      },
    },
  };
};

const prefillPrimaryHomeStatus = (fieldsModel, primaryBorrower) => {
  if (!primaryBorrower?.homeStatus) return fieldsModel;

  return {
    ...fieldsModel,
    primary: {
      ...fieldsModel.primary,
      homeStatus: {
        ...fieldsModel.primary.homeStatus,
        homeStatus: primaryBorrower.homeStatus,
      },
    },
  };
};

function computeInitialFields(context) {
  // cloning fieldsModel as initialFields
  // Note!!: Object.assign only shallow clone objects, nested properties are copied as reference
  let initialFields = JSON.parse(JSON.stringify(fieldsModel));
  // 'agreementsSecondary' is used to persist/validate the status of the agreements for co-borrower, PI1 submit will still send "agreements" key
  initialFields.agreementsSecondary = 'off';

  // prequal
  if (context.prequal) {
    initialFields = prefillPrequal(initialFields, context.prequal);
    context.experiments = { ...context.experiments, ...context.prequal?.experiment };
  }

  // Support Speed-O PI1 prefill feature
  if (shouldPrefillWithMockData(context.environment)) {
    return prefillPI1WithMockData(prefillExperiments(initialFields, context.experiments));
  }

  const dmObj = context.dm;
  if (dmObj) {
    // Experian DM and regular dm
    if (dmObj.isDm) {
      initialFields = dmObj.isExperianDm
        ? prefillExperianDM(initialFields, dmObj.code)
        : prefillDM(initialFields, dmObj);
    }
  }

  // secondLoan borrower (logged in user)
  if (context.isFromSecondLoanFlow) {
    // second loan APP flow to be specific (can include auto loanAPP)
    initialFields = prefillSecondLoan(initialFields, context);
    if (context.isRMEJA) {
      // if user has had a previous issued loan then start by prefill coborrower from that loan
      // seems like can be DRYed logic with pi1.jsx prefilling
      initialFields = prefillSecondLoanJA(initialFields, context);
    }
  }

  // always try to prefill Name and Address if possible
  initialFields = prefillName(initialFields, context.primaryBorrower);
  initialFields = prefillAddress(initialFields, context.primaryBorrower);
  initialFields = prefillExperiments(initialFields, context.experiments);
  initialFields = prefillPrimaryEmail(initialFields, context.primaryBorrower);
  initialFields = prefillPrimaryDob(initialFields, context.primaryBorrower);
  initialFields = prefillPrimaryPhone(initialFields, context.primaryBorrower);
  initialFields = prefillPrimaryIncome(initialFields, context.primaryBorrower);
  initialFields = prefillPrimaryHomeStatus(initialFields, context.primaryBorrower);

  return initialFields;
}

const prefillExperiments = (initialFields, experiments) => {
  // Pass your experiments which you need to persist in lcprop table.
  const result = getExperimentsEnabled(experiments || {});
  return Object.assign({}, initialFields, { ...(Object.keys(result).length > 0 && { experiments: result }) });
};

export default computeInitialFields;

require('/var/lib/jenkins/workspace/borrower-apply-ui_Release_0/src/pages/pi1-1QAAT/pi1-1QAAT.scss');